import { v4 as uuidv4 } from "uuid";
import { OrderItem, OrderItemModifier } from "./types";
import {
  createIframeMessage,
  OrderData,
  ErrorData,
  IframeSource,
} from "@/types/iframe-messages";
import { OrderType } from "@/types/generated/graphql-types";

/**
 * Calculates the total price for cart items including modifiers
 * @param items Array of order items
 * @returns Total price in cents
 */
export const calculateCartTotal = (items: OrderItem[]): number => {
  return items.reduce((total, item) => {
    const basePrice = item.price;
    const modifiersTotal =
      item.modifiers?.reduce((sum, mod) => sum + mod.price, 0) || 0;
    return total + (basePrice + modifiersTotal) * item.qty;
  }, 0);
};

/**
 * Generates a UUID for cart items using uuid v4
 * @returns UUID string
 */
export const generateItemUUID = (): string => {
  return uuidv4();
};

/**
 * Converts GraphQL OrderType to iframe OrderData type
 * @param orderType GraphQL OrderType enum
 * @returns Iframe compatible order type
 */
export const convertOrderType = (
  orderType: OrderType
): "PREORDER" | "DELIVERY" | "PICKUP" => {
  switch (orderType) {
    case OrderType.PreOrder:
      return "PREORDER";
    case OrderType.Delivery:
      return "DELIVERY";
    case OrderType.Pickup:
      return "PICKUP";
    case OrderType.OnTable:
      return "PREORDER"; // ON_TABLE maps to PREORDER for iframe
    default:
      return "PREORDER"; // Default fallback
  }
};

/**
 * Sends iframe messages to parent window
 * @param type Message type
 * @param source Iframe source
 * @param payload Message payload
 */
export const sendIframeMessage = (
  type: "PREORDER_COMPLETED" | "PREORDER_ERROR",
  source: IframeSource,
  payload: OrderData | ErrorData
): void => {
  if (window.parent !== window) {
    const message = createIframeMessage(type, source, payload);
    console.log("Отправка сообщения iframe в родительское окно:", message);
    window.parent.postMessage(message, "*");
  }
};

/**
 * Gets app state from global Redux store for iframe integration
 * @returns App state or null if not available
 */
export const getAppState = (): any => {
  try {
    // Access Redux store from window if available
    const store = (window as any).__REDUX_STORE__;
    return store ? store.getState().app : null;
  } catch (error) {
    console.warn("Could not access app state for iframe message:", error);
    return null;
  }
};

/**
 * Creates OrderData for iframe communication from order items
 * @param items Order items
 * @param order Created order
 * @param appState App state
 * @param currentOrderComment Order comment
 * @returns OrderData for iframe
 */
export const createOrderDataForIframe = (
  items: OrderItem[],
  order: any,
  appState: any,
  currentOrderComment: string
): OrderData => {
  return {
    orderId: order.id,
    items: items.map((item: OrderItem) => ({
      uuid: item.uuid,
      name: item.dish.name,
      quantity: item.qty,
      price: item.price, // Price already in cents
      modifiers:
        item.modifiers?.map((mod) => ({
          id: mod.id,
          name: mod.name,
          price: mod.price, // Price in cents
          type: mod.type,
        })) || [],
      comment: item.comment || "",
    })),
    sum: calculateCartTotal(items), // Calculate sum using utility
    comment: currentOrderComment || "",
    brandId: appState.brandId || "",
    pointId: appState.pointId || "",
    timestamp: Date.now(),
    type: convertOrderType(order.type), // Convert GraphQL OrderType to iframe type
  };
};

/**
 * Creates ErrorData for iframe communication
 * @param error Error message
 * @param cartItems Cart items for context
 * @param cartSum Cart sum for context
 * @returns ErrorData for iframe
 */
export const createErrorDataForIframe = (
  error: string,
  cartItems: number,
  cartSum: number
): ErrorData => {
  return {
    error,
    code: "ORDER_CREATION_FAILED",
    details: {
      timestamp: Date.now(),
      cartItems,
      cartSum,
    },
  };
};
